import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { catchError, EMPTY, firstValueFrom, map, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export type PermissionType = 'Full' | 'None' | 'Read' | 'Creation' | 'Search' | 'Edition';
export type PermissionModule =
  | 'Configuration'
  | 'Incidents'
  | 'Investigations'
  | 'Persons'
  | 'Search';

// Must match the API level names without their "[n] " prefix ("[1] System Administration").
export type AppRole =
  | 'System Administration'
  | 'Oversight'
  | 'Program Leadership'
  | 'Supervision'
  | 'Reporter';

// Latitudinal: what records the user can see
// System = all records; Assigned Programs = only their programs; Self-Created = only their own
export type LatitudinalAccessLevel = 'System' | 'Assigned Programs' | 'Self-Created';

const ROLE_HIERARCHY: AppRole[] = [
  'Reporter',
  'Supervision',
  'Program Leadership',
  'Oversight',
  'System Administration',
];

// API returns values with numeric prefixes, e.g. "[5] Reporter" or "[3] Records Created by Self".
// These helpers strip the prefix and normalize to our canonical types.

function normalizeRole(raw: string): AppRole | null {
  const stripped = raw.replace(/^\[\d+\]\s*/, '').trim();
  return ROLE_HIERARCHY.find((r) => r.toLowerCase() === stripped.toLowerCase()) ?? null;
}

function normalizeLatitudinal(raw: string): LatitudinalAccessLevel {
  const stripped = raw.replace(/^\[\d+\]\s*/, '').toLowerCase();
  if (stripped === 'system' || stripped.startsWith('system w')) return 'System';
  if (stripped.includes('assigned')) return 'Assigned Programs';
  return 'Self-Created';
}

export interface Permission {
  id: string;
  module: string;
  type: PermissionType;
  stage: string;
  section: string;
}

export interface AccessLevel {
  latitudinalAccessLevel: LatitudinalAccessLevel;
  longitudinalAccessLevel: {
    id: string;
    role: AppRole;
    permissions: Permission[];
  };
}

export interface UserProfile {
  accessToken: string;
  tokenType: string;
  expiresAtUtc: string;
  subject: string;
  firstName: string | null;
  email: string;
  relayState: string | null;
  accessLevel: AccessLevel;
}

const TOKEN_KEY = 'auth_token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);

  readonly currentUser = signal<UserProfile | null>(null);

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  getToken(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getUserRole(): AppRole | null {
    const raw = this.currentUser()?.accessLevel?.longitudinalAccessLevel?.role;
    return raw ? normalizeRole(raw) : null;
  }

  getLatitudinalAccess(): LatitudinalAccessLevel | null {
    const raw = this.currentUser()?.accessLevel?.latitudinalAccessLevel;
    return raw ? normalizeLatitudinal(raw) : null;
  }

  /** Returns true if the user's role is minimumRole or higher in the hierarchy. */
  hasRole(minimumRole: AppRole): boolean {
    const userRole = this.getUserRole();
    if (!userRole) return false;
    return ROLE_HIERARCHY.indexOf(userRole) >= ROLE_HIERARCHY.indexOf(minimumRole);
  }

  /**
   * Checks the user's permissions array for a matching entry.
   *
   * Rules:
   * - `types`: at least one must match; 'Full' on the permission always satisfies any requested type.
   * - `stage` (optional): if provided, the permission must cover it.
   *   An empty `p.stage` means "all stages" and matches any requested stage.
   * - `section` (optional): same logic as stage.
   *
   * Examples:
   *   canDo('Incidents', ['Edition'], 'B', 'Middle')  → can edit Middle section while in stage B
   *   canDo('Incidents', ['Full'], 'C')               → has Full access for stage C (Program Leadership+)
   *   canDo('Configuration', ['Full', 'Edition'])     → can write to Configuration
   */
  canDo(
    module: PermissionModule,
    types: PermissionType[],
    stage?: string,
    section?: string,
  ): boolean {
    const permissions = this.currentUser()?.accessLevel?.longitudinalAccessLevel?.permissions ?? [];
    return permissions.some((p) => {
      if (p.module !== module) return false;
      if (p.type === 'None') return false;
      const typeOk = p.type === 'Full' || types.includes(p.type as PermissionType);
      if (!typeOk) return false;
      if (stage !== undefined && p.stage && p.stage !== stage) return false;
      if (section !== undefined && p.section && p.section !== section) return false;
      return true;
    });
  }

  /**
   * Derives the effective permission type the user has for a module+stage.
   *
   * Rule 1 — explicit entry for exactly this stage: highest-ranked type wins across sections.
   * Rule 2 — no explicit entry: if there is a Full permission on an equal-or-higher stage
   *           (or empty stage = all stages), the effective type is Full.
   * Rule 3 — otherwise: null (stage should be hidden).
   *
   * Stage rank: A=1, B=2, C=3, D=4.
   *
   * Example — permissions: Full/C, Read/A
   *   Stage A → Read  (rule 1: explicit Read on A)
   *   Stage B → Full  (rule 2: Full/C covers B because rank(C)=3 ≥ rank(B)=2)
   *   Stage C → Full  (rule 1: explicit Full on C)
   *   Stage D → null  (rule 2 fails: rank(C)=3 < rank(D)=4; no empty-stage Full)
   */
  effectiveAccessForStage(module: PermissionModule, stage: string): PermissionType | null {
    const TYPE_RANK: Partial<Record<PermissionType, number>> = {
      Full: 5,
      Edition: 4,
      Creation: 3,
      Search: 2,
      Read: 1,
    };
    const STAGE_RANK: Record<string, number> = { A: 1, B: 2, C: 3, D: 4 };

    const perms = this.currentUser()?.accessLevel?.longitudinalAccessLevel?.permissions ?? [];
    const modulPerms = perms.filter((p) => p.module === module && p.type !== 'None');

    // Rule 1: explicit entries for exactly this stage
    const explicit = modulPerms.filter((p) => p.stage === stage);
    if (explicit.length > 0) {
      return explicit.reduce<PermissionType>((best, p) => {
        return (TYPE_RANK[p.type as PermissionType] ?? 0) > (TYPE_RANK[best] ?? 0)
          ? (p.type as PermissionType)
          : best;
      }, 'Read');
    }

    // Rule 2: Full on same-or-higher stage, or unrestricted (empty stage)
    const targetRank = STAGE_RANK[stage] ?? 0;
    const coveredByFull = modulPerms.some((p) => {
      if (p.type !== 'Full') return false;
      if (!p.stage) return true; // empty = all stages
      return (STAGE_RANK[p.stage] ?? 0) >= targetRank;
    });

    return coveredByFull ? 'Full' : null;
  }

  /** Returns true if the user can create or edit content for the given module+stage. */
  canInteractWithStage(module: PermissionModule, stage: string): boolean {
    const a = this.effectiveAccessForStage(module, stage);
    return a === 'Full' || a === 'Edition' || a === 'Creation';
  }

  /**
   * Returns true if the user has a Full permission on a stage with HIGHER rank than targetStage
   * (or a stage-unrestricted Full permission). These users can override completion flags and
   * edit fields from earlier stages even after they have been locked.
   */
  canOverrideStageLock(module: PermissionModule, targetStage: string): boolean {
    const STAGE_RANK: Record<string, number> = { A: 1, B: 2, C: 3, D: 4 };
    const targetRank = STAGE_RANK[targetStage] ?? 0;
    const perms = this.currentUser()?.accessLevel?.longitudinalAccessLevel?.permissions ?? [];
    return perms.some((p) => {
      if (p.module !== module || p.type !== 'Full') return false;
      if (!p.stage) return true;
      return (STAGE_RANK[p.stage] ?? 0) > targetRank;
    });
  }

  /** Returns true if the user has any non-None access to the given module. */
  hasModuleAccess(module: string): boolean {
    return this.canDo(module as PermissionModule, [
      'Full',
      'Read',
      'Creation',
      'Edition',
      'Search',
    ]);
  }

  login(options?: { onError?: () => void }): void {
    this.http.get<{ redirectUrl: string }>(`${environment.apiUrl}/api/Saml/login`).subscribe({
      next: ({ redirectUrl }) => (window.location.href = redirectUrl),
      error: () => options?.onError?.(),
    });
  }

  handleCallback(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  restoreSession(): Promise<void> {
    if (!this.isBrowser || !this.getToken()) return Promise.resolve();
    return firstValueFrom(
      this.getAuthUserData().pipe(
        catchError(() => {
          this.logout();
          return EMPTY;
        }),
        map(() => undefined),
      ),
    );
  }

  getAuthUserData(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${environment.apiUrl}/api/auth/me`).pipe(
      tap((user) => {
        this.currentUser.set(user);
        if (user.accessToken) {
          localStorage.setItem(TOKEN_KEY, user.accessToken);
        }
      }),
      map((user) => user),
    );
  }

  logout(): void {
    if (this.isBrowser) localStorage.removeItem(TOKEN_KEY);
    this.currentUser.set(null);
    window.location.reload();
  }
}
